import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import AuthShell from "@/components/feature/AuthShell";
import { supabase } from "@/lib/supabase";
import { verificarAccesoAdmin } from "@/lib/authAdmin";
import { validateOrdinaryPostAuthNext } from "@/lib/authRedirect";

type Status = "loading" | "error";

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState("");
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const code = searchParams.get("code");
    const next = searchParams.get("next");
    const errorDescription = searchParams.get("error_description");

    if (errorDescription) {
      setStatus("error");
      setMessage(errorDescription);
      return;
    }
    if (!code) {
      setStatus("error");
      setMessage("Falta el código de verificación en el enlace.");
      return;
    }

    (async () => {
      try {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          setStatus("error");
          setMessage(error.message);
          return;
        }
        // Recovery must be handled first and never pass the ordinary validator.
        if (data.redirectType === "recovery") {
          navigate("/auth/reset-password", { replace: true });
          return;
        }
        await verificarAccesoAdmin();
        navigate(validateOrdinaryPostAuthNext(next), { replace: true });
      } catch {
        setStatus("error");
        setMessage("No pudimos completar la verificación del acceso.");
      }
    })();
  }, [searchParams, navigate]);

  return (
    <AuthShell>
      <Seo title="Verificación de acceso" path="/auth/callback" robots="noindex, nofollow, noarchive" />
      <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
        {status === "loading" ? (
          <>
            <i className="ri-loader-4-line mx-auto block animate-spin text-3xl leading-none text-primary-500" aria-hidden="true"></i>
            <h1 className="mt-4 text-lg font-heading font-bold text-foreground-950">Verificando tu enlace…</h1>
            <p className="mt-2 text-sm text-foreground-600">Esto solo tomará un momento.</p>
          </>
        ) : (
          <>
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-error-warning-line text-2xl leading-none" aria-hidden="true"></i>
            </span>
            <h1 className="mt-4 text-lg font-heading font-bold text-foreground-950">
              No pudimos verificar el enlace
            </h1>
            <p className="mt-2 text-sm text-foreground-600">{message}</p>
            <Link
              to="/admin/login"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-semibold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
            >
              Ir al inicio de sesión
            </Link>
          </>
        )}
      </div>
    </AuthShell>
  );
}